import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteField,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../config/firebaseConfig";
import { ADMIN_UIDS } from "../Auth/authSlice";

const TEST_UID = "xXN10FCqSHZlkuHNqR5L6ia1jDv1";

// ── Date helpers ──
function toDDMMYYYY(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
}
function inputToFirestore(val) {
  const [y, m, d] = val.split("-");
  return `${d}-${m}-${y}`;
}
function firestoreToInput(val) {
  const [d, m, y] = val.split("-");
  return `${y}-${m}-${d}`;
}

// A versement's declared "dateVers" (the sales day it's for, "YYYY-MM-DD")
// is usually the day BEFORE the calendar day it was submitted on — the
// pompiste reports yesterday's money the next morning. Showing it avoids
// mistaking "submitted on the 16th" for "declared for the 16th".
function formatDateVersIso(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return null;
  return `${d}/${m}/${y}`;
}

// ── Number formatting ──
function parseFrench(str) {
  return parseFloat(
    String(str ?? "")
      .replace(/[\u202F\u00A0 ]/g, "")
      .replace(",", "."),
  );
}
function fmtNum(val) {
  const n = typeof val === "number" ? val : parseFrench(val);
  if (isNaN(n)) return val ?? "—";
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

async function getCurrentUserInfo() {
  const u = getAuth().currentUser;
  if (!u) return null;
  try {
    const snap = await getDoc(doc(db, "app_users", u.uid));
    const name = snap.exists()
      ? snap.data().name
      : u.displayName || u.email || u.uid;
    return { uid: u.uid, name, email: u.email || null };
  } catch {
    return {
      uid: u.uid,
      name: u.displayName || u.email || u.uid,
      email: u.email || null,
    };
  }
}

// ── Category definitions ──
const CATEGORIES = [
  { id: "INDEX", icon: "📊", label: "INDEX" },
  { id: "CUVES", icon: "🛢", label: "CUVES" },
  { id: "VERSEMENT", icon: "💵", label: "VERSEMENT" },
  { id: "APPROV", icon: "🚛", label: "APPROVISIONNEMENT" },
  { id: "RETRAIT_PROD", icon: "📦", label: "RETRAIT PRODUIT" },
  { id: "RETRAIT_ARGENT", icon: "💸", label: "RETRAIT ET REMB. ARGENT" },
  { id: "FERMETURE", icon: "🔒", label: "FERMETURE" },
];

// ── Categorize entries from the data doc ──
function categorizeEntries(data) {
  const result = {};
  for (const cat of CATEGORIES) result[cat.id] = [];

  if (!data) return result;

  for (const [key, val] of Object.entries(data)) {
    if (!val || typeof val !== "object") continue;
    const t = val.type || "";

    if (t === "INDEX") result.INDEX.push({ key, ...val });
    else if (t === "CUVE" || t === "CUVES" || /^[C]\d$/.test(key))
      result.CUVES.push({ key, ...val });
    else if (t === "VERS") result.VERSEMENT.push({ key, ...val });
    else if (key.startsWith("APPROV") || t === "APPROV")
      result.APPROV.push({ key, ...val });
    else if (t === "RETRAIT" || key.startsWith("RETRAIT_PROD"))
      result.RETRAIT_PROD.push({ key, ...val });
    else if (t === "RETRAIT_ARGENT" || key.startsWith("RETRAIT_ARGENT"))
      result.RETRAIT_ARGENT.push({ key, ...val });
    else if (t === "FERMETURE" || key.startsWith("FERM_"))
      result.FERMETURE.push({ key, ...val });
  }

  // Sort INDEX by pump name
  result.INDEX.sort((a, b) =>
    String(a.item || "").localeCompare(String(b.item || "")),
  );
  return result;
}

// ── Get display label for an entry ──
function entryLabel(catId, entry) {
  switch (catId) {
    case "INDEX": {
      const pump = entry.item || entry.key;
      const m = String(entry.key).match(/[SG]\d+_([DA])$/i);
      const moment = m ? m[1].toUpperCase() : "?";
      return `${pump} ${moment === "D" ? "Départ" : moment === "A" ? "Arrivée" : ""}`;
    }
    case "CUVES":
      return entry.item || entry.key;
    case "VERSEMENT":
      return entry.item || entry.key;
    case "APPROV":
      return entry.numbl ? `BL: ${entry.numbl}` : entry.key;
    case "RETRAIT_PROD":
      return entry.item || entry.key;
    case "RETRAIT_ARGENT":
      return entry.item || entry.key;
    case "FERMETURE":
      return entry.name ? `Fermeture (${entry.name})` : entry.key;
    default:
      return entry.key;
  }
}

// ── Get the editable fields for an entry (depends on category) ──
function getEditableFields(catId, entry) {
  switch (catId) {
    case "INDEX":
    case "CUVES":
      return [{ field: "value", label: "Valeur" }];
    case "VERSEMENT":
      return [{ field: "value", label: entry?.item || "Valeur" }];
    case "RETRAIT_PROD":
      return [
        { field: "value", label: "Valeur" },
        { field: "item", label: "Produit" },
        { field: "comment", label: "Commentaire" },
      ];
    case "RETRAIT_ARGENT":
      return [
        { field: "value", label: "Valeur" },
        { field: "item", label: "Type" },
        { field: "comment", label: "Commentaire" },
      ];
    case "APPROV":
      return [
        { field: "qte", label: "Quantité" },
        { field: "numbl", label: "N° BL 1" },
        { field: "numbl2", label: "N° BL 2" },
        { field: "term", label: "Terminal" },
        { field: "produit", label: "Produit" },
        { field: "cuve1", label: "Cuve 1" },
        { field: "avant1", label: "Avant 1" },
        { field: "apres1", label: "Après 1" },
        { field: "cuve2", label: "Cuve 2" },
        { field: "avant2", label: "Avant 2" },
        { field: "apres2", label: "Après 2" },
        { field: "deb", label: "Débordement (L)" },
      ];
    case "FERMETURE": {
      const metaKeys = [
        "type",
        "date",
        "dateFermeture",
        "correctionComment",
        "correctedAt",
      ];
      const labelMap = {
        sortie_su: "Sortie SU",
        sortie_go: "Sortie GO",
        name: "Nom",
      };
      const preferredOrder = [
        "sortie_su",
        "sortie_go",
        "oil_cdf",
        "oil_usd",
        "shop_cdf",
        "shop_usd",
        "gal_cdf",
        "gal_usd",
        "name",
      ];
      const keys = Object.keys(entry).filter((k) => !metaKeys.includes(k));
      keys.sort((a, b) => {
        const ia = preferredOrder.indexOf(a);
        const ib = preferredOrder.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
      const dynamicFields = keys.map((k) => ({
        field: k,
        label: labelMap[k] || k.toUpperCase(),
      }));
      return dynamicFields.length
        ? dynamicFields
        : [{ field: "value", label: "Valeur" }];
    }
    default:
      return [{ field: "value", label: "Valeur" }];
  }
}

// ── Add-mode helpers ──
function getAddFieldDefs(catId) {
  switch (catId) {
    case "INDEX":
      return [
        { field: "pump", label: "Pompe (ex: S1, G2)" },
        { field: "moment", label: "Départ/Arrivée", type: "select", options: [{ value: "D", label: "Départ" }, { value: "A", label: "Arrivée" }] },
        { field: "value", label: "Valeur INDEX" },
      ];
    case "CUVES":
      return [
        { field: "cuve", label: "Cuve (ex: C1, C2)" },
        { field: "value", label: "Niveau (cm)" },
      ];
    case "VERSEMENT":
      return [
        { field: "item", label: "Type (ex: OIL_CDF, SHOP_USD)" },
        { field: "value", label: "Montant" },
      ];
    case "APPROV":
      return [
        { field: "qte", label: "Quantité" },
        { field: "numbl", label: "N° BL 1" },
        { field: "numbl2", label: "N° BL 2" },
        { field: "term", label: "Terminal" },
        { field: "produit", label: "Produit" },
        { field: "cuve1", label: "Cuve 1" },
        { field: "avant1", label: "Avant 1" },
        { field: "apres1", label: "Après 1" },
        { field: "cuve2", label: "Cuve 2" },
        { field: "avant2", label: "Avant 2" },
        { field: "apres2", label: "Après 2" },
        { field: "deb", label: "Débordement (L)" },
      ];
    case "RETRAIT_PROD":
      return [
        { field: "item", label: "Produit" },
        { field: "value", label: "Valeur" },
        { field: "comment", label: "Commentaire" },
      ];
    case "RETRAIT_ARGENT":
      return [
        { field: "item", label: "Type" },
        { field: "value", label: "Valeur" },
        { field: "comment", label: "Commentaire" },
      ];
    case "FERMETURE":
      return [
        { field: "name", label: "Nom" },
        { field: "sortie_su", label: "Sortie SU" },
        { field: "sortie_go", label: "Sortie GO" },
        { field: "oil_cdf", label: "OIL CDF" },
        { field: "oil_usd", label: "OIL USD" },
        { field: "shop_cdf", label: "SHOP CDF" },
        { field: "shop_usd", label: "SHOP USD" },
      ];
    default:
      return [{ field: "value", label: "Valeur" }];
  }
}

function generateKey(catId, addFields) {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  switch (catId) {
    case "INDEX":
      return `${(addFields.pump || "").toUpperCase()}_${addFields.moment || "D"}`;
    case "CUVES":
      return (addFields.cuve || "").toUpperCase();
    case "VERSEMENT":
      return `VERS_${hh}_${mm}_${ss}`;
    case "APPROV":
      return `APPROV_${hh}_${mm}_${ss}`;
    case "RETRAIT_PROD":
      return `RETRAIT_PROD_${hh}_${mm}_${ss}`;
    case "RETRAIT_ARGENT":
      return `RETRAIT_ARGENT_${hh}_${mm}_${ss}`;
    case "FERMETURE":
      return `FERM_${hh}_${mm}_${ss}`;
    default:
      return `ENTRY_${hh}_${mm}_${ss}`;
  }
}

function buildNewEntry(catId, addFields, dateStr) {
  const [d, m, y] = dateStr.split("-");
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  const dateDisplay = `${d}/${m}/${y} ${hh}:${mi}:${ss}`;
  switch (catId) {
    case "INDEX":
      return { type: "INDEX", item: (addFields.pump || "").toUpperCase(), value: addFields.value || "", date: dateDisplay };
    case "CUVES":
      return { type: "CUVES", item: (addFields.cuve || "").toUpperCase(), value: addFields.value || "", date: dateDisplay };
    case "VERSEMENT":
      return { type: "VERS", item: (addFields.item || "").toUpperCase(), value: addFields.value || "", date: dateDisplay };
    case "APPROV": {
      const entry = { type: "APPROV", date: dateDisplay };
      for (const f of ["qte", "numbl", "numbl2", "term", "produit", "cuve1", "avant1", "apres1", "cuve2", "avant2", "apres2", "deb"]) {
        if (addFields[f]) entry[f] = addFields[f];
      }
      return entry;
    }
    case "RETRAIT_PROD":
      return { type: "RETRAIT", item: addFields.item || "", value: addFields.value || "", comment: addFields.comment || "", date: dateDisplay };
    case "RETRAIT_ARGENT":
      return { type: "RETRAIT_ARGENT", item: addFields.item || "", value: addFields.value || "", comment: addFields.comment || "", date: dateDisplay };
    case "FERMETURE": {
      const entry = { type: "FERMETURE", date: dateDisplay };
      for (const f of ["name", "sortie_su", "sortie_go", "oil_cdf", "oil_usd", "shop_cdf", "shop_usd"]) {
        if (addFields[f] !== undefined) entry[f] = addFields[f];
      }
      return entry;
    }
    default:
      return { type: catId, value: addFields.value || "", date: dateDisplay };
  }
}

export default function AdminCorrection() {
  const navigate = useNavigate();
  const [stations, setStations] = useState([]);
  const [selectedUid, setSelectedUid] = useState("");
  const [dateInput, setDateInput] = useState(
    firestoreToInput(toDDMMYYYY(new Date())),
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);

  // Step state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedEntryKey, setSelectedEntryKey] = useState(null);
  const [fieldEdits, setFieldEdits] = useState({});
  const [comment, setComment] = useState("");

  // Add mode
  const [addMode, setAddMode] = useState(false);
  const [addFields, setAddFields] = useState({});
  const [addSaving, setAddSaving] = useState(false);

  // Load station list
  useEffect(() => {
    async function loadStations() {
      const snap = await getDocs(collection(db, "users"));
      const list = [];
      const seen = new Set();
      snap.forEach((d) => {
        if (ADMIN_UIDS.includes(d.id)) return;
        const u = d.data();
        const key = String(u.station || "").toUpperCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        list.push({ uid: d.id, station: u.station });
      });
      list.sort((a, b) => (a.station || "").localeCompare(b.station || ""));
      // Add TEST station (its UID is an admin account, so it's filtered above)
      list.push({ uid: TEST_UID, station: "TEST" });
      setStations(list);
      if (list.length) setSelectedUid(list[0].uid);
    }
    loadStations();
  }, []);

  const categorized = categorizeEntries(data);

  // Auto-load data when station or date changes
  useEffect(() => {
    if (!selectedUid || !dateInput) return;
    let cancelled = false;
    async function autoLoad() {
      setLoading(true);
      setError(null);
      setData(null);
      setSelectedCategory(null);
      setSelectedEntryKey(null);
      setSaveMsg(null);
      try {
        const dateStr = inputToFirestore(dateInput);
        const snap = await getDoc(
          doc(db, "users", selectedUid, "data", dateStr),
        );
        if (cancelled) return;
        if (snap.exists()) {
          setData(snap.data());
        } else {
          setData({});
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Erreur lors du chargement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    autoLoad();
    return () => {
      cancelled = true;
    };
  }, [selectedUid, dateInput]);

  const handleSelectCategory = (catId) => {
    setSelectedCategory(catId);
    setSelectedEntryKey(null);
    setFieldEdits({});
    setComment("");
    setAddMode(false);
    setAddFields({});
  };

  const handleSelectEntry = (key) => {
    setSelectedEntryKey(key);
    setFieldEdits({});
    setComment("");
    setConfirmDelete(false);
    setAddMode(false);
    setAddFields({});
  };

  const currentEntry =
    selectedEntryKey && selectedCategory
      ? categorized[selectedCategory]?.find((e) => e.key === selectedEntryKey)
      : null;

  const editableFields = currentEntry
    ? getEditableFields(selectedCategory, currentEntry)
    : [];

  const stationName =
    stations.find((st) => st.uid === selectedUid)?.station || "";

  const handleSave = async () => {
    if (!selectedUid || !selectedEntryKey || !currentEntry) return;
    if (Object.keys(fieldEdits).length === 0) return;
    setSaving(true);
    setError(null);
    setSaveMsg(null);
    try {
      const dateStr = inputToFirestore(dateInput);
      const docRef = doc(db, "users", selectedUid, "data", dateStr);
      const updatedEntry = {
        ...currentEntry,
        ...fieldEdits,
        correctionComment: comment || null,
        correctedAt: new Date().toISOString(),
      };
      const payload = { [selectedEntryKey]: updatedEntry };
      await setDoc(docRef, payload, { merge: true });

      // Log correction for Slack notification
      const changes = Object.entries(fieldEdits).map(([field, newValue]) => ({
        field,
        label: editableFields.find((ef) => ef.field === field)?.label || field,
        oldValue: currentEntry[field],
        newValue,
      }));
      const correctedBy = await getCurrentUserInfo();
      await addDoc(collection(db, "corrections"), {
        stationUid: selectedUid,
        station: stationName,
        date: dateStr,
        category: selectedCategory,
        entryKey: selectedEntryKey,
        entryLabel: entryLabel(selectedCategory, currentEntry),
        changes,
        comment: comment || null,
        correctedBy,
        createdAt: new Date().toISOString(),
      });

      // Refresh data
      const snap = await getDoc(docRef);
      if (snap.exists()) setData(snap.data());
      const labels = Object.keys(fieldEdits)
        .map((f) => editableFields.find((ef) => ef.field === f)?.label || f)
        .join(", ");
      setSaveMsg(
        `✅ ${labels} corrigé(s) pour « ${entryLabel(selectedCategory, currentEntry)} »`,
      );
      setFieldEdits({});
      setComment("");
      setSelectedEntryKey(null);
    } catch (e) {
      setError(e.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUid || !selectedEntryKey || !currentEntry) return;
    setDeleting(true);
    setError(null);
    setSaveMsg(null);
    try {
      const dateStr = inputToFirestore(dateInput);
      const docRef = doc(db, "users", selectedUid, "data", dateStr);
      await updateDoc(docRef, { [selectedEntryKey]: deleteField() });

      const correctedBy = await getCurrentUserInfo();
      const deletedValues = editableFields.map((f) => ({
        field: f.field,
        label: f.label,
        value: currentEntry[f.field] ?? null,
      }));
      await addDoc(collection(db, "corrections"), {
        action: "DELETE",
        stationUid: selectedUid,
        station: stationName,
        date: dateStr,
        category: selectedCategory,
        entryKey: selectedEntryKey,
        entryLabel: entryLabel(selectedCategory, currentEntry),
        deletedValues,
        comment: comment || null,
        correctedBy,
        createdAt: new Date().toISOString(),
      });

      const snap = await getDoc(docRef);
      setData(snap.exists() ? snap.data() : {});
      setSaveMsg(
        `✅ Entrée « ${entryLabel(selectedCategory, currentEntry)} » supprimée`,
      );
      setFieldEdits({});
      setComment("");
      setSelectedEntryKey(null);
      setConfirmDelete(false);
    } catch (e) {
      setError(e.message || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedUid || !selectedCategory) return;
    setAddSaving(true);
    setError(null);
    setSaveMsg(null);
    try {
      const dateStr = inputToFirestore(dateInput);
      const key = generateKey(selectedCategory, addFields);
      const entry = buildNewEntry(selectedCategory, addFields, dateStr);
      const docRef = doc(db, "users", selectedUid, "data", dateStr);
      await setDoc(docRef, { [key]: entry }, { merge: true });

      const correctedBy = await getCurrentUserInfo();
      await addDoc(collection(db, "corrections"), {
        action: "ADD",
        stationUid: selectedUid,
        station: stationName,
        date: dateStr,
        category: selectedCategory,
        entryKey: key,
        newEntry: entry,
        comment: comment || null,
        correctedBy,
        createdAt: new Date().toISOString(),
      });

      const snap = await getDoc(docRef);
      if (snap.exists()) setData(snap.data());
      setSaveMsg(`✅ Entrée ajoutée : ${key}`);
      setAddMode(false);
      setAddFields({});
      setComment("");
    } catch (e) {
      setError(e.message || "Erreur lors de l'ajout");
    } finally {
      setAddSaving(false);
    }
  };

  return (
    <div style={s.root} className="admin-menu-root">
      <div style={s.card} className="admin-menu-card">
        {/* Header */}
        <div style={s.header}>
          <button style={s.backBtn} onClick={() => navigate("/menu")}>
            ← Menu
          </button>
          <div>
            <div style={s.title}>✏️ CORRECTION DES DONNÉES</div>
            <div style={s.subtitle}>{stationName} — Correction guidée</div>
          </div>
        </div>

        {/* Step 1: Date + Station */}
        <div style={s.stepHeader}>
          <span style={s.stepNum}>1</span> Date & Station
        </div>
        <div style={s.selRow} className="mobile-stack">
          <div style={s.selGroup}>
            <label style={s.label}>Station</label>
            <select
              style={s.select}
              value={selectedUid}
              onChange={(e) => setSelectedUid(e.target.value)}
            >
              {stations.map((st) => (
                <option key={st.uid} value={st.uid}>
                  {st.station}
                </option>
              ))}
            </select>
          </div>
          <div style={s.selGroup}>
            <label style={s.label}>Date</label>
            <input
              type="date"
              style={s.dateInput}
              value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
            />
          </div>
        </div>

        {loading && <div style={s.empty}>Chargement…</div>}

        {error && <div style={s.errorBox}>{error}</div>}
        {saveMsg && <div style={s.successBox}>{saveMsg}</div>}

        {/* Step 2: Category */}
        {data && (
          <>
            <div style={s.stepHeader}>
              <span style={s.stepNum}>2</span> Catégorie
            </div>
            <div style={s.selGroup}>
              <select
                style={s.select}
                value={selectedCategory || ""}
                onChange={(e) => handleSelectCategory(e.target.value)}
              >
                <option value="" disabled>
                  Sélectionner…
                </option>
                {CATEGORIES.map((cat) => {
                  const count = categorized[cat.id]?.length || 0;
                  return (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon} {cat.label} ({count})
                    </option>
                  );
                })}
              </select>
            </div>
          </>
        )}

        {/* Step 3: Field selection + Ajouter */}
        {selectedCategory && (
          <>
            {categorized[selectedCategory]?.length > 0 && (
              <>
                <div style={s.stepHeader}>
                  <span style={s.stepNum}>3</span> Champs —{" "}
                  {CATEGORIES.find((c) => c.id === selectedCategory)?.label}
                </div>
                <div style={s.selGroup}>
                  <select
                    style={s.select}
                    value={selectedEntryKey || ""}
                    onChange={(e) => handleSelectEntry(e.target.value)}
                  >
                    <option value="" disabled>
                      Sélectionner…
                    </option>
                    {categorized[selectedCategory].map((entry) => (
                      <option key={entry.key} value={entry.key}>
                        {entryLabel(selectedCategory, entry)} —{" "}
                        {selectedCategory === "APPROV"
                          ? `Qte: ${fmtNum(entry.qte)} — BL: ${entry.bl || "—"}`
                          : selectedCategory === "FERMETURE"
                            ? `SU: ${fmtNum(entry.sortie_su)} — GO: ${fmtNum(entry.sortie_go)}`
                            : fmtNum(entry.value)}
                        {selectedCategory === "VERSEMENT" && entry.dateVers
                          ? ` (pour le ${formatDateVersIso(entry.dateVers)})`
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {!addMode && (
              <button
                style={s.addBtn}
                onClick={() => {
                  setAddMode(true);
                  setSelectedEntryKey(null);
                  setFieldEdits({});
                  setComment("");
                  setAddFields({});
                }}
              >
                + Ajouter une entrée
              </button>
            )}

            {addMode && (
              <>
                <div style={s.stepHeader}>
                  <span style={{ ...s.stepNum, background: "#22c55e" }}>+</span>{" "}
                  Nouvelle entrée —{" "}
                  {CATEGORIES.find((c) => c.id === selectedCategory)?.label}
                </div>
                <div style={s.editPanel}>
                  {getAddFieldDefs(selectedCategory).map((f) =>
                    f.type === "select" ? (
                      <div key={f.field} style={s.editRow}>
                        <label style={s.label}>{f.label}</label>
                        <select
                          style={s.select}
                          value={addFields[f.field] ?? (f.options?.[0]?.value || "")}
                          onChange={(e) =>
                            setAddFields((prev) => ({ ...prev, [f.field]: e.target.value }))
                          }
                        >
                          {f.options?.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div key={f.field} style={s.editRow}>
                        <label style={s.label}>{f.label}</label>
                        <input
                          type="text"
                          style={s.valueInput}
                          placeholder={f.label}
                          value={addFields[f.field] ?? ""}
                          onChange={(e) =>
                            setAddFields((prev) => ({ ...prev, [f.field]: e.target.value }))
                          }
                        />
                      </div>
                    )
                  )}
                  <div style={{ ...s.stepHeader, marginTop: 12 }}>
                    <span style={{ ...s.stepNum, background: "#22c55e" }}>✎</span> Commentaire
                  </div>
                  <div style={s.editRow}>
                    <textarea
                      style={s.commentInput}
                      placeholder="Raison de l'ajout…"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <button
                      style={{ ...s.saveBtn, background: "#22c55e", flex: 1 }}
                      onClick={handleAdd}
                      disabled={addSaving}
                    >
                      {addSaving ? "Ajout…" : "✅ Enregistrer l'entrée"}
                    </button>
                    <button
                      style={{ ...s.confirmNo, padding: "12px 16px" }}
                      onClick={() => { setAddMode(false); setAddFields({}); setComment(""); }}
                      disabled={addSaving}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Step 4 & 5 & 6: Value + Comment + Save */}
        {currentEntry && (
          <>
            <div style={s.stepHeader}>
              <span style={s.stepNum}>4</span> Modifier la valeur
            </div>
            {selectedCategory === "VERSEMENT" && currentEntry.dateVers && (
              <div style={{ ...s.subtitle, marginBottom: 12 }}>
                Versement déclaré pour le{" "}
                <strong>{formatDateVersIso(currentEntry.dateVers)}</strong>{" "}
                (saisi le {inputToFirestore(dateInput).replaceAll("-", "/")})
              </div>
            )}
            <div style={s.editPanel}>
              {editableFields.map((f) => (
                <div key={f.field} style={s.editRow}>
                  <label style={s.label}>{f.label}</label>
                  <div style={s.currentValue}>
                    {String(currentEntry[f.field] ?? "—")}
                  </div>
                  <input
                    type="text"
                    style={s.valueInput}
                    placeholder="Nouvelle valeur"
                    value={fieldEdits[f.field] ?? ""}
                    onChange={(e) =>
                      setFieldEdits((prev) => ({
                        ...prev,
                        [f.field]: e.target.value,
                      }))
                    }
                  />
                </div>
              ))}

              <div style={{ ...s.stepHeader, marginTop: 20 }}>
                <span style={s.stepNum}>5</span> Commentaire
              </div>
              <div style={s.editRow}>
                <textarea
                  style={s.commentInput}
                  placeholder="Raison de la correction…"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                />
              </div>

              <button
                style={{
                  ...s.saveBtn,
                  ...(Object.keys(fieldEdits).length === 0 || saving
                    ? s.saveBtnDisabled
                    : {}),
                }}
                onClick={handleSave}
                disabled={Object.keys(fieldEdits).length === 0 || saving}
              >
                {saving ? "Sauvegarde…" : "✏️ Enregistrer la correction"}
              </button>

              {!confirmDelete ? (
                <button
                  style={s.deleteBtn}
                  onClick={() => setConfirmDelete(true)}
                  disabled={deleting || saving}
                >
                  🗑️ Supprimer cette entrée
                </button>
              ) : (
                <div style={s.confirmBox}>
                  <span style={s.confirmText}>
                    Confirmer la suppression de « {entryLabel(selectedCategory, currentEntry)} » ?
                  </span>
                  <textarea
                    style={s.commentInput}
                    placeholder="Raison de la suppression… (obligatoire)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={2}
                  />
                  <div style={s.confirmBtns}>
                    <button
                      style={{
                        ...s.confirmYes,
                        ...(!comment.trim() || deleting ? { opacity: 0.5, cursor: "not-allowed" } : {}),
                      }}
                      onClick={handleDelete}
                      disabled={!comment.trim() || deleting}
                    >
                      {deleting ? "Suppression…" : "Oui, supprimer"}
                    </button>
                    <button
                      style={s.confirmNo}
                      onClick={() => setConfirmDelete(false)}
                      disabled={deleting}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  root: {
    minHeight: "100vh",
    background: "#0f172a",
    padding: 24,
  },
  card: {
    background: "#1e293b",
    borderRadius: 16,
    padding: "32px 28px",
    maxWidth: 700,
    margin: "0 auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 24,
  },
  backBtn: {
    background: "transparent",
    border: "1px solid #334155",
    borderRadius: 8,
    color: "#94a3b8",
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  title: {
    fontSize: 18,
    fontWeight: 800,
    color: "#e2e8f0",
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 3,
    letterSpacing: 0.5,
  },
  stepHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 700,
    color: "#a855f7",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginBottom: 12,
    marginTop: 20,
  },
  stepNum: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "#a855f7",
    color: "white",
    fontSize: 12,
    fontWeight: 800,
  },
  selRow: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
    flexWrap: "wrap",
    padding: "16px",
    background: "#0f172a",
    borderRadius: 10,
  },
  selGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 11,
    color: "#64748b",
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  select: {
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: 8,
    color: "#e2e8f0",
    padding: "8px 12px",
    fontSize: 13,
    cursor: "pointer",
    minWidth: 140,
  },
  dateInput: {
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: 8,
    color: "#e2e8f0",
    padding: "8px 12px",
    fontSize: 13,
  },
  errorBox: {
    background: "#7f1d1d20",
    border: "1px solid #ef444440",
    borderRadius: 8,
    color: "#ef4444",
    padding: "10px 14px",
    marginBottom: 16,
    fontSize: 13,
  },
  successBox: {
    background: "#064e3b20",
    border: "1px solid #22c55e40",
    borderRadius: 8,
    color: "#22c55e",
    padding: "10px 14px",
    marginBottom: 16,
    fontSize: 13,
  },
  catGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    gap: 10,
  },
  catBtn: {
    background: "#0f172a",
    border: "1px solid #334155",
    borderRadius: 10,
    padding: "14px 10px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    transition: "border-color .15s, background .15s",
    color: "#e2e8f0",
    position: "relative",
  },
  catBtnActive: {
    borderColor: "#a855f7",
    background: "#a855f720",
  },
  catBtnEmpty: {
    opacity: 0.4,
    cursor: "not-allowed",
  },
  catIcon: {
    fontSize: 22,
  },
  catLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#e2e8f0",
    textAlign: "center",
    lineHeight: 1.3,
  },
  catCount: {
    fontSize: 10,
    color: "#64748b",
    fontWeight: 700,
    background: "#1e293b",
    borderRadius: 4,
    padding: "1px 6px",
    marginTop: 2,
  },
  fieldList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  fieldBtn: {
    background: "#0f172a",
    border: "1px solid #334155",
    borderRadius: 8,
    padding: "10px 14px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    transition: "border-color .15s",
    color: "#e2e8f0",
  },
  fieldBtnActive: {
    borderColor: "#a855f7",
    background: "#a855f710",
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#e2e8f0",
  },
  fieldValue: {
    fontSize: 12,
    color: "#64748b",
    fontFamily: "monospace",
    textAlign: "right",
  },
  editPanel: {
    background: "#0f172a",
    borderRadius: 10,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  editRow: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  currentValue: {
    fontSize: 14,
    color: "#64748b",
    fontFamily: "monospace",
    padding: "8px 12px",
    background: "#1e293b",
    borderRadius: 6,
    border: "1px solid #334155",
  },
  valueInput: {
    background: "#1e293b",
    border: "1px solid #a855f7",
    borderRadius: 6,
    color: "#e2e8f0",
    padding: "10px 12px",
    fontSize: 14,
    fontFamily: "monospace",
  },
  commentInput: {
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: 6,
    color: "#e2e8f0",
    padding: "10px 12px",
    fontSize: 13,
    resize: "vertical",
    fontFamily: "inherit",
  },
  saveBtn: {
    background: "#a855f7",
    border: "none",
    borderRadius: 8,
    color: "white",
    padding: "12px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: 0.5,
    marginTop: 4,
  },
  saveBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  empty: {
    textAlign: "center",
    color: "#475569",
    padding: "24px 0",
    fontSize: 13,
  },
  addBtn: {
    background: "transparent",
    border: "1px solid #22c55e60",
    borderRadius: 8,
    color: "#22c55e",
    padding: "10px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 12,
    width: "100%",
  },
  deleteBtn: {
    background: "transparent",
    border: "1px solid #ef444460",
    borderRadius: 8,
    color: "#ef4444",
    padding: "10px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 4,
  },
  confirmBox: {
    background: "#7f1d1d20",
    border: "1px solid #ef444440",
    borderRadius: 8,
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 4,
  },
  confirmText: {
    fontSize: 13,
    color: "#ef4444",
    fontWeight: 600,
  },
  confirmBtns: {
    display: "flex",
    gap: 8,
  },
  confirmYes: {
    background: "#ef4444",
    border: "none",
    borderRadius: 6,
    color: "white",
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  confirmNo: {
    background: "transparent",
    border: "1px solid #334155",
    borderRadius: 6,
    color: "#94a3b8",
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
};
